import React, { useCallback, useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

// 認定コントリビューター（Backlog U-2 / U-3）。
//
// **システムは候補を出すだけで、自動では付与しない。** 運営が感覚で
// 「この人は良さそう」と付けると問い合わせ対応が増えるので、条件を満たした人
// だけをここに並べ、運営が確認して付与する。
//
// **閾値は画面に出さない。** 出るのは候補者の実測値（確認160件・一致92%）で、
// 何件で通るのかは書かない。基準表を画面に貼ると、スクリーンショットが外に
// 出た時点で公開したのと同じになる。
//
// **タブが2枚あるのが要点。** 候補は「まだ持っていない人」なので、認定した
// 瞬間に候補から消える。「認定済み」を並べて見られないと、取り消したいときに
// その人を探せない。

const TABS = [
  { key: 'candidates', label: '候補' },
  { key: 'certified', label: '認定済み' },
];

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

const daysAgo = (iso) => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86400000);
  if (days <= 0) return '今日';
  if (days === 1) return '昨日';
  return `${days}日前`;
};

const shortDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export default function CertificationCandidates() {
  useAuthGuard();

  const [tab, setTab] = useState('candidates');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState('');

  // 取り消しは理由が必須なので、押した瞬間ではなく入力欄を開く
  const [revoking, setRevoking] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');

  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);

  const load = useCallback(async (which) => {
    setLoading(true);
    setError('');
    try {
      const path = which === 'certified' ? 'certified' : 'candidates';
      const res = await apiFetch(`${API_BASE_URL}/admin/contributors/${path}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setRows(body?.data?.[which === 'certified' ? 'contributors' : 'candidates'] || []);
    } catch (e) {
      setError(e.message || '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
    setRevoking(null);
    setHistoryFor(null);
  }, [load, tab]);

  const decide = async (userId, action, reason) => {
    setBusyId(userId);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/contributors/${userId}/certification`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reason: reason || '' }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      setNotice(action === 'grant' ? '認定しました' : '認定を取り消しました');
      setRevoking(null);
      setRevokeReason('');
      await load(tab);
    } catch (e) {
      setError(e.message || '操作に失敗しました');
    } finally {
      setBusyId(null);
    }
  };

  const openHistory = async (userId) => {
    if (historyFor === userId) {
      setHistoryFor(null);
      return;
    }
    setHistoryFor(userId);
    setHistory([]);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/contributors/${userId}/certification`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setHistory(body?.data?.history || []);
    } catch (e) {
      setError(e.message || '履歴を読めませんでした');
    }
  };

  const certifiedTab = tab === 'certified';

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="h4 mb-1">認定コントリビューター</h1>
          <p className="text-muted small mb-0">
            継続的な現地確認・証跡・他者との整合・直近の活動をすべて満たした方が候補に出ます。
            <strong>自動では付与しません。</strong>
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => load(tab)}
        >
          再読み込み
        </button>
      </div>

      <ul className="nav nav-tabs mb-3">
        {TABS.map((t) => (
          <li className="nav-item" key={t.key}>
            <button
              type="button"
              className={`nav-link ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      {notice && <div className="alert alert-success py-2">{notice}</div>}
      {error && <div className="alert alert-danger py-2">{error}</div>}

      {loading ? (
        <div className="text-muted">読み込み中…</div>
      ) : rows.length === 0 ? (
        // **0件は異常ではない。** 条件が厳しいので、初期は誰も通らないのが普通。
        // 「読み込み失敗」と読み違えられないように、はっきり書く。
        <div className="alert alert-secondary">
          {certifiedTab
            ? 'まだどなたも認定していません。'
            : 'いま条件を満たしている方はいません。基準が厳しいため、初期はこの状態が通常です（読み込みは成功しています）。'}
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>ユーザー</th>
                  <th className="text-end">現地確認</th>
                  <th className="text-end">店舗数</th>
                  <th className="text-end">他者との一致</th>
                  <th className="text-end">証跡率</th>
                  <th className="text-end">最終活動</th>
                  <th className="text-end">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <React.Fragment key={c.userId}>
                    <tr>
                      <td>
                        <div className="fw-semibold">{c.displayName || '(名前未設定)'}</div>
                        {c.handle && <div className="text-muted small">@{c.handle}</div>}
                      </td>
                      <td className="text-end">
                        {c.onSiteObservations.toLocaleString()}
                        <div className="text-muted small">
                          全{c.observations.toLocaleString()}件中
                        </div>
                      </td>
                      <td className="text-end">{c.distinctStores}</td>
                      <td className="text-end">
                        {pct(c.agreementRate)}
                        {/* **分母を必ず添える。** 1件中1件の100%と60件中55件の92%が
                            同じ顔で並ぶと、運営が前者を上に見てしまう */}
                        <div className="text-muted small">{c.comparableCount}件で比較</div>
                      </td>
                      <td className="text-end">{pct(c.evidenceRate)}</td>
                      <td className="text-end">{daysAgo(c.lastActiveAt)}</td>
                      <td className="text-end text-nowrap">
                        {certifiedTab ? (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm me-1"
                            disabled={busyId === c.userId}
                            onClick={() => {
                              setRevoking(c.userId);
                              setRevokeReason('');
                            }}
                          >
                            取り消す
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm me-1"
                            disabled={busyId === c.userId}
                            onClick={() => decide(c.userId, 'grant')}
                          >
                            認定する
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => openHistory(c.userId)}
                        >
                          履歴
                        </button>
                      </td>
                    </tr>

                    {revoking === c.userId && (
                      <tr className="table-warning">
                        <td colSpan={7}>
                          {/* **取り消しには理由が要る。** 理由の無い取消は、本人にも
                              次の運営にも説明できない */}
                          <div className="d-flex gap-2 align-items-center flex-wrap">
                            <label className="form-label mb-0 small text-nowrap">
                              取り消しの理由（必須）
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              style={{ maxWidth: 420 }}
                              maxLength={500}
                              value={revokeReason}
                              onChange={(e) => setRevokeReason(e.target.value)}
                              placeholder="例: 同一景品の連投が判明"
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              disabled={!revokeReason.trim() || busyId === c.userId}
                              onClick={() => decide(c.userId, 'revoke', revokeReason.trim())}
                            >
                              取り消す
                            </button>
                            <button
                              type="button"
                              className="btn btn-link btn-sm"
                              onClick={() => setRevoking(null)}
                            >
                              やめる
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {historyFor === c.userId && (
                      <tr className="table-light">
                        <td colSpan={7}>
                          {history.length === 0 ? (
                            <span className="text-muted small">履歴はありません。</span>
                          ) : (
                            <ul className="list-unstyled mb-0 small">
                              {history.map((h) => (
                                <li key={h.id} className="mb-1">
                                  <span
                                    className={`badge me-2 ${h.action === 'grant' ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'}`}
                                  >
                                    {h.action === 'grant' ? '認定' : '取り消し'}
                                  </span>
                                  <span className="text-muted me-2">
                                    {shortDateTime(h.createdAt)}
                                  </span>
                                  {h.reason && <span>{h.reason}</span>}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-muted small mt-3 mb-0">
        バッジは1種類のみです（段階はありません）。認定基準は、不正利用防止のため
        公開していません。
      </p>
    </Layout>
  );
}
